import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';
import { bearerTokenHeaders } from '../authorization-headers';

export interface ISignerVerification {
  loggedInName: string;
  // null when the tiltakshaver is an organisation — there is no name to compare against.
  developerNameMatches: boolean | null;
  // null when the tiltakshaver is a private individual — there is no organisation to act for.
  authorizedForDeveloperOrganization: boolean | null;
  developerName: string;
  developerOrganizationName: string;
}

// 'unknown' means the lookup was never started - there is no application id yet, or nobody is
// logged in. 'loading' is a lookup in flight, which callers must not treat as a passed check.
export type SignerVerificationState =
  | { status: 'unknown' }
  | { status: 'loading' }
  | { status: 'loaded'; verification: ISignerVerification }
  | { status: 'unavailable' };

@Injectable({
  providedIn: 'root'
})
export class SignerVerificationService {

  private readonly endpointApplicationPath = 'api/application';
  private readonly stateSubject = new BehaviorSubject<SignerVerificationState>({ status: 'unknown' });
  private requestedApplicationId: string | null = null;

  state$: Observable<SignerVerificationState>;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly oauthService: OAuthService,
  ) {
    this.state$ = this.stateSubject.asObservable();
  }

  // Idempotent per application so several callers can ask for it without causing extra
  // round trips to Altinn.
  ensureLoaded(applicationId: string): void {
    if (!applicationId || this.requestedApplicationId === applicationId || !this.oauthService.hasValidAccessToken()) {
      return;
    }

    this.requestedApplicationId = applicationId;
    this.stateSubject.next({ status: 'loading' });
    const endpoint = `${environment.apiUrl}/${this.endpointApplicationPath}/${applicationId}/altinn3/signer`;

    // Only present for a private individual signer - lets the backend resolve which Altinn
    // party is actually "self" when Altinn reports more than one. Absent (rather than required)
    // because an organisation signer has no pid claim at all. Sent as a POST body rather than a
    // query parameter since this is a Norwegian birth number (fødselsnummer).
    const birthNo = this.oauthService.getIdentityClaims()?.['pid'] ?? null;

    this.httpClient.post<ISignerVerification>(endpoint, { birthNo }, { headers: bearerTokenHeaders(this.oauthService) }).subscribe({
      next: verification => this.stateSubject.next({ status: 'loaded', verification }),
      error: () => {
        // Reported as unavailable rather than as a passed check, so the UI can say so instead
        // of implying the signer was verified.
        this.requestedApplicationId = null;
        this.stateSubject.next({ status: 'unavailable' });
      },
    });
  }
}
